/**
 * Watchdog for the integration-sync state.
 *
 * WHY THIS EXISTS. `App.sync.step` is set on the first SYNC_PROGRESS event and cleared only by
 * SYNC_COMPLETE or SYNC_ERROR. One dropped terminal event — a socket blip, a server restart
 * mid-sync, or a manual chain whose last job is gated off by `shouldRunStrategy` so nothing ever
 * emits SYNC_COMPLETE — leaves the step (and the org's `syncing = 1` setting) stuck forever.
 *
 * It used to be far worse than a stuck chip: `Layout` rendered the loading screen INSTEAD OF THE
 * ENTIRE APP while that step was truthy, so a dropped event locked every user in the org out of the
 * dashboard. The sync is non-blocking now — it is a row in the notifications popover, see
 * `library/hooks/useBackgroundJobs` — so the failure mode is a spinner that never stops. Still wrong, still worth recovering from.
 *
 * The timer is re-armed on each progress event, so `timeoutMs` bounds the GAP BETWEEN steps, never
 * the sync as a whole: a long sync that keeps reporting is never interrupted.
 *
 * Firing is not destructive. The caller's `onTimeout` re-reads org settings rather than asserting
 * the sync is over, so a sync that really is still running keeps its indicator up. That matters more
 * than it looks — `selectSyncInProgress` derives from the org SYNC settings and NOTHING in the app
 * polls them; they refresh on auth and on sync socket events only. So the store can hold a
 * "syncing = 1" snapshot taken at boot and never revisit it, and clearing the step alone would not
 * clear the indicator. Asking the server is what actually recovers.
 */

/**
 * Two minutes, measured against the slowest single step of an IR/Inception/Control/TTLock sync —
 * not against a whole sync. Raise it if a legitimate step is ever seen to exceed this; the symptom
 * would be the loader dropping mid-sync and then being restored by the settings re-read.
 */
export const SYNC_IDLE_TIMEOUT_MS = 120000;

/**
 * @param {Object}   opts
 * @param {Function} opts.onTimeout  called once when the idle window elapses
 * @param {number}  [opts.timeoutMs] override, for tests
 * @returns {{ arm: Function, disarm: Function, isArmed: Function }}
 */
export const createSyncWatchdog = ({ onTimeout, timeoutMs = SYNC_IDLE_TIMEOUT_MS }) => {
	let timer = null;

	const disarm = () => {
		if (timer !== null) {
			clearTimeout(timer);
			timer = null;
		}
	};

	return {
		// Re-arming replaces any pending timer, so progress events keep pushing the deadline out.
		arm() {
			disarm();
			timer = setTimeout(() => {
				timer = null;
				onTimeout();
			}, timeoutMs);
		},
		disarm,
		isArmed: () => timer !== null
	};
};
